import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import SmsIcon from '@mui/icons-material/Sms';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import { formatPriceWithCurrency } from '../../../utils/vat';
import useFinancingStatus from './useFinancingStatus';
import { OPEN_STATUSES, formatTime, resursResultText } from './resursLabels';

/**
 * Resurs financing: the workshop enters the customer's mobile number, Resurs texts the
 * customer a link (Resurs Tili or Osamaksu, bank ID, signing), and the dialog waits for
 * the outcome. The workshop never opens the payment page itself.
 *
 * Steps follow ``invoice.resurs_payment``: no open request -> form; open -> waiting (polls
 * financing-status every 5 s); finished after being started here -> result.
 */
const ResursFinancingDialog = ({ t, open, onClose, invoice, onInvoiceUpdated, onRefresh, onNotify }) => {
  const payment = invoice?.resurs_payment;
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [startedHere, setStartedHere] = useState(false);

  useEffect(() => {
    if (open) {
      setPhone(invoice?.customer_phone || '');
      setEmail(invoice?.customer_email || '');
      setError('');
      setStartedHere(!!(payment && OPEN_STATUSES.includes(payment.status)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const waiting = !!(payment && OPEN_STATUSES.includes(payment.status));
  const finished = startedHere && payment && !waiting;

  useFinancingStatus(
    invoice?.id,
    open && waiting,
    (data) => {
      if ((data.resurs_payment && data.resurs_payment.status) !== payment?.status) onRefresh();
    },
    { intervalMs: 5000 },
  );

  const handleSend = async () => {
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.post(`/api/invoices/${invoice.id}/resurs-financing/`, { phone, email });
      setStartedHere(true);
      onInvoiceUpdated(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t('resurs.sendFailed'));
      if (err.response?.data?.error_code === 'outcome_unknown') {
        // Resurs did not answer: the request stays open and is checked automatically.
        setStartedHere(true);
        onRefresh();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setBusy('resend');
    try {
      const response = await axios.post(`/api/invoices/${invoice.id}/resurs-financing-resend/`, { phone });
      onInvoiceUpdated(response.data);
      setError('');
      onNotify(t('resurs.smsResent'), 'success');
    } catch (err) {
      onNotify(err.response?.data?.error || t('resurs.sendFailed'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleCancelRequest = async () => {
    setBusy('cancel');
    try {
      const response = await axios.post(`/api/invoices/${invoice.id}/financing-cancel/`);
      onInvoiceUpdated(response.data);
    } catch (err) {
      onNotify(err.response?.data?.error || t('financing.cancelFailed'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const total = invoice?.total_incl_vat;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="resurs-financing-dialog">
      <DialogTitle>{t('resurs.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t('financing.amount')}: <strong>{formatPriceWithCurrency(total)}</strong>
        </Typography>

        {!waiting && !finished && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">{t('resurs.howItWorks')}</Alert>
            <TextField
              label={t('resurs.phoneLabel')}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              fullWidth
              size="small"
              type="tel"
              autoComplete="off"
              inputProps={{ 'data-testid': 'resurs-phone-input' }}
            />
            <TextField
              label={t('resurs.emailLabel')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              size="small"
              type="email"
              autoComplete="off"
            />
            {error && <Alert severity="error" data-testid="resurs-error">{error}</Alert>}
          </Box>
        )}

        {waiting && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2, textAlign: 'center' }} data-testid="resurs-waiting">
            <CircularProgress />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('resurs.waitingTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('resurs.waitingText', { phone: payment.phone_masked })}
            </Typography>
            {payment.expires_at && (
              <Typography variant="body2" color="text.secondary">
                {t('resurs.linkValidUntil', { time: formatTime(payment.expires_at) })}
              </Typography>
            )}
            {error && <Alert severity="warning" sx={{ textAlign: 'left' }}>{error}</Alert>}
            <TextField
              label={t('resurs.resendPhoneLabel')}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              size="small"
              type="tel"
              sx={{ maxWidth: 280, width: '100%' }}
            />
          </Box>
        )}

        {finished && (
          <Alert severity={payment.status === 'paid' ? 'success' : 'warning'} data-testid="resurs-result">
            {resursResultText(t, payment.status)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
        {!waiting && !finished && (
          <>
            <Button onClick={onClose} disabled={submitting}>{t('Cancel')}</Button>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={submitting || !phone.trim()}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SmsIcon />}
              data-testid="resurs-send-button"
            >
              {t('resurs.sendLink')}
            </Button>
          </>
        )}
        {waiting && (
          <>
            <Button
              color="error"
              onClick={handleCancelRequest}
              disabled={!!busy}
              startIcon={busy === 'cancel' ? <CircularProgress size={16} color="inherit" /> : null}
              data-testid="resurs-cancel-button"
            >
              {t('financing.cancelRequest')}
            </Button>
            <Button
              onClick={handleResend}
              disabled={!!busy || !phone.trim()}
              startIcon={busy === 'resend' ? <CircularProgress size={16} color="inherit" /> : <SmsIcon />}
              data-testid="resurs-resend-button"
            >
              {t('resurs.resendSms')}
            </Button>
            <Button variant="contained" onClick={onClose}>{t('Close')}</Button>
          </>
        )}
        {finished && (
          <Button variant="contained" onClick={onClose}>{t('Close')}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default withTranslation()(ResursFinancingDialog);
